import { Rect, Transformer } from "react-konva";

import {
  DraggableItem,
  DraggableItemObj,
  RenderElProps,
} from "./draggable-item.class";
import { useEffect, useRef, useState } from "react";

import {
  DeleteButton,
  DuplicateElButton,
  MoveUpperButton,
  MoveLowerButton,
  BorderColorButton,
  FillColorButton,
  ReticulaButton,
} from "../components/draggable-menu-btns";
import { Html } from "react-konva-utils";
import { ItemMenuContainer } from "./comic.class";
import { Reticula } from "../api/documento.types";
import { ladrilhoDaTrama } from "./trama-do-quadro";

type RectangleObj = Omit<DraggableItemObj, "type"> & {
  borderWidth: number;
  borderColor: string;
  fillColor: string;
  /**
   * O raio do canto. Entrou junto com os estilos de quadro (T1.23).
   *
   * É opcional porque TODO quadrinho já salvo no Firestore foi gravado sem este campo, e
   * `undefined` tem que continuar significando "canto reto". Tornar obrigatório quebraria a
   * leitura de tudo que já existe — e a migração de dados de criança é a última coisa que se
   * quer fazer por causa de um raio de canto.
   */
  cornerRadius?: number;
  /**
   * A RETÍCULA DESTE QUADRO — a metade de "em qualquer um dos campos" do `T2.108`.
   *
   * Opcional pela MESMA razão do `cornerRadius`, e a razão é boa: todo quadrinho já salvo no
   * Firestore foi gravado sem este campo, e `undefined` tem que continuar significando "sem
   * retícula". Presumir trama mudaria o desenho de quem nunca pediu.
   */
  reticula?: Reticula;
};
export class Rectangle extends DraggableItem {
  borderWidth: number;
  borderColor: string;
  fillColor: string;
  cornerRadius: number;
  reticula: Reticula;

  protected menuButtons = [
    DeleteButton,
    DuplicateElButton,
    MoveUpperButton,
    MoveLowerButton,
    BorderColorButton,
    FillColorButton,
    ReticulaButton,
  ];

  constructor(obj: RectangleObj) {
    super({ ...obj, type: "rectangles" }, "");
    this.borderWidth = obj.borderWidth;
    this.borderColor = obj.borderColor;
    this.fillColor = obj.fillColor;
    this.cornerRadius = obj.cornerRadius ?? 0;
    this.reticula = obj.reticula ?? "nenhuma";
  }

  override async waitImgLoad() {
    return;
  }

  toObj() {
    return {
      ...super.toObj(),
      borderWidth: this.borderWidth,
      borderColor: this.borderColor,
      fillColor: this.fillColor,
      cornerRadius: this.cornerRadius,
      reticula: this.reticula,
    };
  }

  /**
   * Trocar a retícula deste quadro.
   *
   * Diferente de `setFillColor`, que fala com o nó do Konva direto: aqui a mudança precisa de um
   * `Image` NOVO (o ladrilho muda de opacidade), e imagem carrega de forma assíncrona. Quem
   * redesenha é o `useEffect` do `render`, que observa este campo.
   */
  setReticula(value: Reticula) {
    this.reticula = value;
  }

  setBorderColor(value: string) {
    this.borderColor = value;
    if (!!this.imageRef) {
      this.imageRef.setAttr("stroke", value);
    }
  }
  setFillColor(value: string) {
    this.fillColor = value;
    if (!!this.imageRef) {
      this.imageRef.setAttr("fill", value);
    }
  }

  render(props: RenderElProps): JSX.Element {
    const El = () => {
      const shapeRef: React.MutableRefObject<any> = useRef();
      const trRef: React.MutableRefObject<any> = useRef();
      const itemMenuRef = useRef<HTMLDivElement>(null);

      const [itemMenuEl, setItemMenuEl] = useState<HTMLDivElement | null>(null);

      /**
       * O LADRILHO DA TRAMA, JÁ CARREGADO.
       *
       * `fillPatternImage` do Konva quer uma `Image` pronta — passar uma que ainda está
       * carregando pinta NADA e não avisa. Por isso o estado: a imagem entra quando o `onload`
       * chega, e só aí o quadro se redesenha com ela.
       *
       * As dependências são a retícula E a cor de fundo: a cor entra DENTRO do ladrilho (ver
       * `trama-do-quadro.ts`), então trocar o preenchimento com a trama ligada precisa de um
       * ladrilho novo, senão a cor antiga fica congelada na textura.
       */
      const [ladrilho, setLadrilho] = useState<HTMLImageElement | null>(null);
      // `this.reticula` e `this.fillColor` saem para variaveis ANTES do efeito, e nao e estilo:
      // o `react-hooks/exhaustive-deps` recusa expressao composta no array de dependencias
      // ("complex expression in the dependency array"), porque nao consegue conferi-la
      // estaticamente. Com `CI=true` — que e como a Vercel builda — esse aviso VIRA ERRO e o
      // deploy falha. Medido do jeito mais caro: falhou no deploy, e nao aqui.
      const reticulaAtual = this.reticula;
      const corDeFundoAtual = this.fillColor;
      useEffect(() => {
        const uri = ladrilhoDaTrama(reticulaAtual, corDeFundoAtual);
        if (!uri) {
          setLadrilho(null);
          return;
        }
        const img = new window.Image();
        let vivo = true;
        img.onload = () => {
          // `vivo` porque o item pode ser apagado ou a página trocada enquanto o SVG carrega, e
          // escrever estado num componente desmontado é o aviso que ninguém lê e o vazamento que
          // ninguém acha.
          if (vivo) setLadrilho(img);
        };
        img.src = uri;
        return () => {
          vivo = false;
        };
      }, [reticulaAtual, corDeFundoAtual]);

      useEffect(() => {
        this.imageRef = shapeRef.current;
        setItemMenuEl(itemMenuRef.current);

        if (props.isSelected) {
          // we need to attach transformer manually
          trRef.current.nodes([shapeRef.current]);
          trRef.current.getLayer().batchDraw();
        }
      }, []);

      const scaleX = props.scaleX ?? 1;
      const scaleY = props.scaleY ?? 1;

      return (
        <>
          <Rect
            ref={shapeRef}
            draggable={props.isSelected}
            x={this.x * scaleX}
            y={this.y * scaleY}
            width={this.width * scaleX}
            height={this.height * scaleY}
            rotation={this.rotation}
            stroke={this.borderColor}
            strokeWidth={this.borderWidth}
            fill={this.fillColor}
            {...(ladrilho
              ? {
                  // `fillPatternImage` SUBSTITUI o `fill` — e é por isso que a cor de fundo vai
                  // dentro do ladrilho. Ligar a trama não pode apagar o preenchimento que a
                  // criança escolheu.
                  //
                  // A escala compensa o zoom da folha: sem ela, a trama cresceria junto com a
                  // página e um quadro ampliado ficaria com pontos do tamanho de bolinhas. O
                  // ponto é acabamento de impressão — ele tem tamanho FÍSICO, não relativo.
                  fillPatternImage: ladrilho,
                  fillPatternRepeat: "repeat",
                  fillPatternScale: { x: 1 / scaleX, y: 1 / scaleY },
                  // `fillPriority` E O QUE FAZ A TRAMA APARECER, e sem ele nada acontece.
                  //
                  // Com `fill` E `fillPatternImage` no mesmo nó, o Konva escolhe um pelo
                  // `fillPriority`, que por omissão é `"color"` — e a cor ganha. MEDIDO em
                  // 14/09: o padrão estava setado no nó (`fillPatternImage()` devolvia a
                  // imagem), o rótulo do botão trocava, e a folha continuava branca.
                  //
                  // Um defeito que não aparece em nenhum log: tudo "funcionou", e não pintou.
                  fillPriority: "pattern",
                }
              : {})}
            // Sem esta linha o `cornerRadius` seria guardado e nunca DESENHADO — o estilo
            // "Lembrança" existiria no dado e não na tela.
            cornerRadius={this.cornerRadius}
            onClick={props.onClick}
            onDragMove={(e) => props.onDragMove(e, itemMenuRef.current)}
            onDragEnd={props.onDragEnd}
            onTouchStart={(e) => this.onTouchStart(e)}
            onTouchEnd={(e) => this.onTouchEnd(e, props.onClick)}
            onTouchMove={(e) => {
              this.onTouchMove(e, !props.isSelected);
            }}
          />

          {props.isSelected && (
            <>
              {/* `transform={false}` — E ISSO CONSERTA UM DEFEITO MEDIDO.

                  O `<Html>` do react-konva aplica a transformacao do PALCO no `<div>` que ele
                  cria, e o palco carrega o ZOOM da folha. Medido em 13/09, com a folha a 91%: os
                  botoes do menu do item, declarados com 44px, chegavam a tela com 40 — 44 x 0,91.
                  A 24% (uma A4 numa janela baixa) seriam 10px.

                  Alvo de toque que encolhe conforme o zoom da folha nao e alvo de toque. E o menu
                  ja se posiciona em COORDENADAS DE TELA (`getItemMenuPos`) num container
                  `position: fixed` — a transformacao do palco por cima disso era escala a mais,
                  nao posicionamento. */}
              <Html
                transform={false}
                // O `<Html>` cria um `<div>` DENTRO do container do palco. Com a transformacao
                // ligada ele o deixa `position: absolute`; com `transform={false}` ele LIMPA o
                // estilo, e o div cai no fluxo normal — ocupando espaco e COBRINDO parte do
                // canvas. Medido: depois de trocar para `transform={false}`, os quadros 2 e 3 da
                // tirinha pararam de selecionar, porque o div comia o clique.
                //
                // Zerado e absoluto, ele volta a nao ocupar nada. O menu em si e `position:
                // fixed` e se posiciona sozinho, entao nao precisa de caixa nenhuma.
                divProps={{
                  style: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: 0,
                    height: 0,
                  },
                }}
              >
                <ItemMenuContainer
                  ref={itemMenuRef}
                  style={this.getItemMenuPos(itemMenuEl)}
                >
                  {this.getMenuBts(props)}
                </ItemMenuContainer>
              </Html>

              <Transformer
                ref={trRef}
                boundBoxFunc={(oldBox, newBox) =>
                  props.boundBoxFunc(oldBox, newBox, itemMenuRef.current)
                }
                onTransformEnd={props.onTransformEnd}
              />
            </>
          )}
        </>
      );
    };
    return <El />;
  }
}
